import { FormControl } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandEnum } from "../../_common/enum/brand.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/formTemplateType.enum";
import { TrackingStageEnum } from "../../_common/enum/trackingStage.enum";
import { IQuoteTrackingDetail } from "../../_common/interface/quoteTrackingDetail/iQuoteTrackingDetail.interface";
import {
    inferAttributionFromQueryParams,
    mergeTrackingParams,
    normalizeTrackingParam,
    hasTrackingParamSignal,
} from "../../_helpers/attribution.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { sendBodyHeightToParent } from "../../_helpers/iFrameSize.helper";
import {
    applyPersistedParentContext,
    bootstrapParamsFromReferrer,
    drainEarlyParentContextQueue,
    getConfiguredParentOrigins,
    handleParentContextMessage,
    persistParentPathname,
    requestParentContextFromIframe,
} from "../../_helpers/parentContext.helper";
import {
    getOrCreateTrackingSnapshot,
    getOrCreateVisitId,
    registerFormCreationForVisit,
    saveFormAttribution,
} from "../../_helpers/trackingSnapshot.helper";
import { formService } from "../../_services/form.service";
import { quoteTracking } from "../../_services/quoteTracking.service";
import { withFormAccessToken } from "../../_helpers/formAccessToken.helper";

const TRACKING_GRACE_MS = 1200;
const POLL_INTERVAL_MS = 250;
const MAX_POLL_ATTEMPTS = 40;

export function Forms(params: { brand: BrandEnum }) {
    const navigate = useNavigate();

    const resetTrackingData = (): IQuoteTrackingDetail => ({
        formId: null,
        brandId: params.brand,
        formTypeId: "",
        quoteId: "",
        trackingStageId: null,
        source: "",
        originalSource: "",
        medium: "",
        originalMedium: "",
        campaign: "",
        originalCampaign: "",
        keyword: "",
        originalKeyword: "",
        ip: "",
        trafficSource: "",
        originalReferringUrl: "",
        handlLandingPage: "",
        handlLandingPageBase: "",
        handlRef: "",
        handlOriginalRef: "",
        handlRefDomain: "",
        handlUrl: "",
        handlUrlBase: "",
        handlID: "",
        gClId: "",
        metaFbc: "",
        metaFbp: "",
        metaFbclid: "",
        isFormDuplicate: false,
        formSequenceNo: null,
        visitId: null
    });

    const [isLoading, setLoading] = useState(false);
    const [formTypeId, setFormTypeId] = useState<string | null>(null);
    const [trackingData, setTrackingData] = useState<IQuoteTrackingDetail>(resetTrackingData);

    const createdRef = useRef(false);
    const queryParamsRef = useRef<URLSearchParams>(new URLSearchParams(""));
    const pathnameRef = useRef<string>("");
    const pathnameKnownAtRef = useRef<number | null>(null);
    const parentOriginsRef = useRef<string[]>(getConfiguredParentOrigins());

    function getFormTypeFromPath(path: string): string | null {
        if (!path) return null;

        const p = path.toLowerCase();

        // More specific paths first (e.g. executive-income-protection contains income-protection)
        if (p.includes("executive-income-protection")) return FormTemplateTypeEnum.ExecutiveIncomeProtection.toString();
        if (p.includes("mortgage-protection")) return FormTemplateTypeEnum.MortgageProtection.toString();
        if (p.includes("life-insurance")) return FormTemplateTypeEnum.LifeInsurance.toString();
        if (p.includes("income-protection")) return FormTemplateTypeEnum.IncomeProtection.toString();
        if (p.includes("illness")) return FormTemplateTypeEnum.SeriousIllnessProtection.toString();
        if (p.includes("mortgage") && !p.includes("protection")) return FormTemplateTypeEnum.Mortgages.toString();
        if (p.includes("health-application")) return FormTemplateTypeEnum.HealthQuestionnaire.toString();

        return null;
    }

    function resolveFormTypeId(): string | null {
        const qp = queryParamsRef.current;

        // Product form type comes from the current WordPress page path (or explicit formTypeId).
        // Handl URLs are first-touch attribution only — never used to pick the form template.
        if (pathnameRef.current) {
            const fromPath = getFormTypeFromPath(pathnameRef.current);
            if (fromPath) return fromPath;
        }

        const fromQuery = qp.get("formTypeId");
        if (fromQuery) return fromQuery;

        return null;
    }

    function canResolveFormTypeFromCurrentPage(): boolean {
        return resolveFormTypeId() !== null;
    }

    function ingestAllBootstrapSources() {
        drainEarlyParentContextQueue(pathnameRef, queryParamsRef);

        const boot = bootstrapParamsFromReferrer();
        if (boot?.pathname) {
            pathnameRef.current = boot.pathname;
            persistParentPathname(boot.pathname);
        }

        applyPersistedParentContext(pathnameRef, queryParamsRef);

        if (boot) {
            queryParamsRef.current = mergeTrackingParams(queryParamsRef.current, boot.searchParams);
        }
    }

    function isTrackingGraceElapsed(): boolean {
        if (!pathnameKnownAtRef.current) return false;
        return Date.now() - pathnameKnownAtRef.current >= TRACKING_GRACE_MS;
    }

    function markPathnameKnown() {
        if (!pathnameKnownAtRef.current) {
            pathnameKnownAtRef.current = Date.now();
        }
    }

    function tryResolveAndCreate(options?: { force?: boolean }) {
        if (createdRef.current) return;

        const qp = queryParamsRef.current;
        const existingToken = qp.get("token");

        if (existingToken) {
            createdRef.current = true;
            navigate(`/formOriginal?token=${encodeURIComponent(existingToken)}`);
            return;
        }

        const resolvedFormTypeId = resolveFormTypeId();

        if (!resolvedFormTypeId) {
            return;
        }

        markPathnameKnown();

        const force = options?.force === true;
        const hasSignal = hasTrackingParamSignal(qp);
        const graceElapsed = isTrackingGraceElapsed();

        if (!force && !hasSignal && !graceElapsed) {
            return;
        }

        createdRef.current = true;

        setFormTypeId(resolvedFormTypeId);
        setTrackingData((current) => ({
            ...current,
            formTypeId: resolvedFormTypeId
        }));

        createNewForm(parseInt(resolvedFormTypeId, 10), queryParamsRef.current);
    }

    useEffect(() => {
        ingestAllBootstrapSources();

        if (canResolveFormTypeFromCurrentPage()) {
            tryResolveAndCreate({ force: true });
        }

        requestParentContextFromIframe();
    }, []);

    useEffect(() => {
        if (formTypeId) return;

        let attempts = 0;

        const tick = () => {
            if (formTypeId || createdRef.current) {
                clearInterval(interval);
                return;
            }

            requestParentContextFromIframe();
            tryResolveAndCreate();

            attempts++;

            const formTypeReady = canResolveFormTypeFromCurrentPage();
            const shouldForce =
                attempts >= MAX_POLL_ATTEMPTS ||
                (formTypeReady && isTrackingGraceElapsed());

            if (shouldForce && formTypeReady) {
                clearInterval(interval);
                tryResolveAndCreate({ force: true });
            }
        };

        const interval = setInterval(tick, POLL_INTERVAL_MS);
        tick();

        return () => clearInterval(interval);
    }, [formTypeId]);

    useEffect(() => {
        const allowed = parentOriginsRef.current;

        const handleMessage = (event: MessageEvent) => {
            if (createdRef.current && (event.data || {}).type !== "REQUEST_HEIGHT") {
                return;
            }

            const result = handleParentContextMessage(
                event,
                pathnameRef,
                queryParamsRef,
                allowed
            );

            if (result.handled) {
                if (result.handled && pathnameRef.current) {
                    markPathnameKnown();
                }

                if (event.data?.type === "PARENT_PATHNAME") {
                    tryResolveAndCreate({ force: true });
                    return;
                }

                tryResolveAndCreate();
                return;
            }

            switch ((event.data || {}).type) {
                case "REQUEST_HEIGHT":
                    sendBodyHeightToParent();
                    return;
                default:
                    requestParentContextFromIframe();
                    return;
            }
        };

        window.addEventListener("message", handleMessage);
        window.addEventListener("resize", sendBodyHeightToParent);

        return () => {
            window.removeEventListener("message", handleMessage);
            window.removeEventListener("resize", sendBodyHeightToParent);
        };
    }, []);

    function createNewForm(formTypeId: FormTemplateTypeEnum, queryParams: URLSearchParams) {
        setLoading(true);

        return formService
            .createByType(formTypeId, params.brand)
            .then(
                async ({ id, token }) => {
                    const snapshotParams = getOrCreateTrackingSnapshot(queryParams);
                    const { creationIndex, isDuplicate } = registerFormCreationForVisit();
                    const visitId = getOrCreateVisitId();

                    saveFormAttribution(id, snapshotParams, visitId);

                    await createQuoteTracking(
                        id,
                        snapshotParams,
                        TrackingStageEnum.FormCreated,
                        isDuplicate,
                        creationIndex,
                        visitId
                    );

                    if (
                        formTypeId === FormTemplateTypeEnum.HealthQuestionnaire ||
                        formTypeId === FormTemplateTypeEnum.FactFind
                    ) {
                        navigate(withFormAccessToken(`/formOriginal/${id}`, id, token));
                    } else {
                        navigate(withFormAccessToken(`${id}`, id, token));
                    }
                },
                (err) => handleErrorResponse(err)
            )
            .finally(() => setLoading(false));
    }

    async function createQuoteTracking(
        id: number,
        queryParams: URLSearchParams,
        trackingStageId: TrackingStageEnum,
        isDuplicate: boolean,
        creationIndex?: number,
        visitId?: string
    ) {
        const attribution = inferAttributionFromQueryParams(queryParams);

        const payload: IQuoteTrackingDetail = {
            ...trackingData,
            formId: id,
            brandId: params.brand,
            trackingStageId,
            source: attribution.source,
            originalSource: attribution.originalSource,
            medium: attribution.medium,
            originalMedium: attribution.originalMedium,
            campaign: attribution.campaign,
            originalCampaign: attribution.originalCampaign,
            keyword: attribution.keyword,
            originalKeyword: attribution.originalKeyword,
            ip: normalizeTrackingParam(queryParams.get("handl_ip")),
            gClId: attribution.gclid,
            handlLandingPage: attribution.handlLandingPage,
            handlLandingPageBase: normalizeTrackingParam(queryParams.get("handl_landing_page_base")),
            handlRef: normalizeTrackingParam(queryParams.get("handl_ref")),
            handlOriginalRef: attribution.handlOriginalRef,
            handlRefDomain: attribution.handlRefDomain,
            handlUrl: normalizeTrackingParam(queryParams.get("handl_url")),
            handlUrlBase: normalizeTrackingParam(queryParams.get("handl_url_base")),
            handlID: normalizeTrackingParam(queryParams.get("handlID")),
            trafficSource: attribution.trafficSource,
            originalReferringUrl: "",
            metaFbc: attribution.fbc,
            metaFbp: attribution.fbp,
            metaFbclid: attribution.fbclid,
            isFormDuplicate: isDuplicate,
            formSequenceNo: creationIndex ?? null,
            visitId: visitId ?? null
        };

        setTrackingData(payload);

        try {
            await quoteTracking.post(payload);
        } catch (err: any) {
            handleErrorResponse(err);
        }
    }

    return (
        <div aria-busy={isLoading || undefined}>
            <FormControl fullWidth variant="outlined" />
        </div>
    );
}

export default Forms;
